import React, { useState } from 'react';
import SEO from '../../components/SEO';
import SiteFooter from '../../components/SiteFooter';
import DirectAnswer from '../../components/DirectAnswer';
import { Link } from 'react-router-dom';
import { dentistActonPageSEO } from '../../data/seo';
import { 
  getMedicalConditionLD, 
  getMedicalProcedureLD, 
  getBreadcrumbLD,
  getFAQPageLD 
} from '../../data/structuredData';
import { preventiveCareCondition, preventiveCareProcedure } from '../../data/medicalSchemas';
import { generalDentistryFAQs } from '../../data/faqs';
import { dentistActonBreadcrumbs } from '../../data/breadcrumbs';
import { Button } from '../../components/ui/button';
import MiniConsultButton from '../../components/MiniConsultButton';
import EnquireButton from '../../components/EnquireButton';
import { 
  Phone, 
  Smile,
  CheckCircle2,
  Clock,
  MapPin,
  Instagram,
  Facebook,
  Twitter,
  Music2,
  Heart,
  Shield,
  Award,
  RotateCcw,
  ImageIcon
} from 'lucide-react';
import WhatsAppIcon from '@/components/WhatsAppIcon';

// Service Card Data with detailed information
const serviceData = {
  examination: {
    title: "Initial Examination",
    icon: Award,
    shortDesc: "Comprehensive check-up including oral health assessment, X-rays if needed, and treatment plan.",
    features: [
      "Full oral health examination",
      "Digital X-rays if clinically needed",
      "Oral cancer screening",
      "Gum health assessment",
      "Treatment plan discussion",
      "No-pressure consultation",
      "Same-day appointments available"
    ],
    hasExample: false
  },
  cleaning: {
    title: "Hygiene & Cleaning",
    icon: Clock,
    shortDesc: "Professional cleaning to remove plaque and tartar. Keep your smile bright and healthy.",
    features: [
      "Professional scale and polish",
      "Stain removal (coffee, tea, red wine)",
      "Plaque and tartar removal",
      "Gum disease prevention",
      "Fresh breath guarantee",
      "Personalized oral hygiene advice",
      "Gentle, pain-free experience"
    ],
    hasExample: true,
    examplePlaceholder: "/api/placeholder/400/300"
  },
  fillings: {
    title: "Fillings & Restorations",
    icon: Shield,
    shortDesc: "White composite fillings that blend naturally with your teeth. No more silver fillings!",
    features: [
      "Tooth-colored composite materials",
      "Mercury-free (no amalgam)",
      "Natural-looking results",
      "Same-day treatment",
      "Long-lasting durability",
      "Minimal tooth preparation",
      "Matches your natural tooth shade perfectly"
    ],
    hasExample: true,
    examplePlaceholder: "/api/placeholder/400/300"
  },
  family: {
    title: "Family Dentistry",
    icon: Heart,
    shortDesc: "Gentle care for all ages, from children to seniors. Making dental visits stress-free.",
    features: [
      "All ages welcome (3+ years)",
      "Child-friendly environment",
      "Gentle, patient approach",
      "Family appointment scheduling",
      "Preventive care focus",
      "Anxiety-free dentistry",
      "Building positive dental habits"
    ],
    hasExample: true,
    examplePlaceholder: "/api/placeholder/400/300"
  }
};

const ServiceFlipCard = ({ serviceKey, data }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const Icon = data.icon;

  return (
    <div className="relative h-[400px] perspective-1000">
      <div
        className={`relative w-full h-full transition-all duration-500 transform-style-3d ${
          isFlipped ? 'rotate-y-180' : ''
        }`}
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        {/* Front of Card */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl border-2 border-sky-gray/20 hover:border-[#01A9B4] transition-all cursor-pointer backface-hidden"
          style={{ backfaceVisibility: 'hidden' }}
          onClick={() => setIsFlipped(true)}
        >
          <Icon className="w-12 h-12 text-burnt-coral mb-4" />
          <h3 className="text-xl font-bold text-rabbit mb-2">{data.title}</h3>
          <p className="text-chocolate-malt mb-4">{data.shortDesc}</p>
          <p className="text-sm text-[#01A9B4] font-semibold">Click to learn more →</p>
        </div>

        {/* Back of Card */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-[#01A9B4]/10 to-white p-6 rounded-xl border-2 border-[#01A9B4] backface-hidden overflow-y-auto"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }}
        >
          {!showExample ? (
            <>
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-[#006A71]">{data.title}</h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFlipped(false);
                  }}
                  className="text-[#006A71] hover:text-burnt-coral"
                >
                  <RotateCcw className="w-5 h-5" />
                </button>
              </div>

              {serviceKey === 'examination' ? (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-[#006A71] mb-3">
                    What Your Examination Includes:
                  </p>
                  {data.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-[#01A9B4] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-[#006A71] mb-3">
                    What Makes Our {data.title} Special:
                  </p>
                  {data.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-[#01A9B4] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              )}

              {data.hasExample && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowExample(true);
                  }}
                  size="sm"
                  className="w-full mt-4 bg-[#01A9B4] hover:bg-[#006A71] text-white"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  See Example
                </Button>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-bold text-[#006A71]">Example Result</h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowExample(false);
                  }}
                  className="text-[#006A71] hover:text-burnt-coral"
                >
                  <RotateCcw className="w-5 h-5" />
                </button>
              </div>
              
              <div className="bg-gray-200 rounded-lg h-48 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <ImageIcon className="w-12 h-12 mx-auto mb-2" />
                  <p className="text-sm">Example Image Placeholder</p>
                  <p className="text-xs mt-1">Before/After photos coming soon</p>
                </div>
              </div>
              
              <p className="text-xs text-gray-600">
                *Actual results may vary. Photos will be added soon.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DentistActon = () => {
  const phoneNumber = '07787814064';
  const whatsappNumber = '447787814064';
  const phoneLink = `tel:${phoneNumber}`;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const whatsappLink = isMobile
    ? `whatsapp://send?phone=${whatsappNumber}&text=Hi, I'd like to book a dental appointment`
    : `https://web.whatsapp.com/send?phone=${whatsappNumber}&text=Hi, I'd like to book a dental appointment`;

  return (
    <>
      {/* SEO Meta Tags & Structured Data */}
      <SEO
        title={dentistActonPageSEO.title}
        description={dentistActonPageSEO.description}
        path={dentistActonPageSEO.path}
        keywords={dentistActonPageSEO.keywords}
        image={dentistActonPageSEO.image}
        structuredData={[
          getMedicalConditionLD(preventiveCareCondition),
          getMedicalProcedureLD(preventiveCareProcedure),
          getBreadcrumbLD(dentistActonBreadcrumbs),
          getFAQPageLD(generalDentistryFAQs),
        ]}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-sky-gray/20 shadow-sm">
        <div className="bg-gradient-to-r from-burnt-coral/5 to-sky-gray/10 border-b border-sky-gray/20">
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-between">
              <a href={phoneLink} className="flex items-center gap-2 text-sm md:text-base text-rabbit hover:text-burnt-coral transition-colors group">
                <Phone className="w-4 h-4 md:w-5 md:h-5 text-burnt-coral group-hover:scale-110 transition-transform" />
                <span className="font-semibold">07787 814 064</span>
              </a>
              <div className="flex items-center gap-3 md:gap-4">
                <a href="https://www.instagram.com/kartikpateldentist/" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on Instagram">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://www.facebook.com/profile.php?id=61588647254315" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on Facebook">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="https://twitter.com/restore_dental" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on X (Twitter)">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="https://www.tiktok.com/@restore.dental" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on TikTok">
                  <Music2 className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Smile className="w-8 h-8 text-burnt-coral" />
            <span className="text-xl md:text-2xl font-bold text-rabbit">restore.dental</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/">
              <Button variant="outline" size="sm" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white">
                Home
              </Button>
            </Link>
            <Button size="sm" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild>
              <a href={whatsappLink}>Book Now</a>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 bg-gradient-to-br from-[#01A9B4]/10 via-white to-[#006A71]/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-[#006A71]/10 rounded-full border-2 border-[#01A9B4]/20">
              <p className="text-sm font-medium text-[#006A71]">
                <MapPin className="w-4 h-4 inline mr-1" />
                Acton, West London W3
              </p>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-rabbit mb-6">
              Your Trusted Dentist in <span className="text-burnt-coral">Acton</span>
            </h1>
            <p className="text-xl md:text-2xl text-chocolate-malt mb-8">
              Comprehensive dental care for you and your family
            </p>
            <DirectAnswer
              question="Looking for a private dentist in Acton?"
              answer="Restore.Dental is a private dental practice at 64a Horn Lane, Acton, London W3 6NP, led by Dr Kartik Patel BDS (GDC 177993) with 15+ years' experience. We provide check-ups, hygiene, white fillings, crowns, root canals, Invisalign, implants and cosmetic dentistry, with a free new-patient consultation, 0% finance and same-day emergency appointments. 4.9★ on Google."
              bullets={['New patients welcome', 'Free consultation with treatment', 'Open Mon–Sat', '4.9★ Google rating']}
              className="max-w-3xl mx-auto mb-8"
            />
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Button size="lg" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild>
                <a href={whatsappLink}>
                  <WhatsAppIcon className="w-5 h-5 mr-2" />
                  Book Appointment
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" asChild>
                <a href={phoneLink}>
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </a>
              </Button>
            <EnquireButton slug="general-checkup" about="General Checkup" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" testId="hero-enquire-btn" />
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-chocolate-malt">
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-burnt-coral" />
                <span>Gentle, Patient-Centered Care</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-burnt-coral" />
                <span>Same-Day Appointments</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-burnt-coral" />
                <span>Modern, Hygienic Facilities</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Services */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-6">
                General Dentistry Services in Acton
              </h2>
              <p className="text-gray-600 mb-8 text-center">Click any card to learn more about how we deliver exceptional care</p>
              <div className="grid md:grid-cols-2 gap-6">
                <ServiceFlipCard serviceKey="examination" data={serviceData.examination} />
                <ServiceFlipCard serviceKey="cleaning" data={serviceData.cleaning} />
                <ServiceFlipCard serviceKey="fillings" data={serviceData.fillings} />
                <ServiceFlipCard serviceKey="family" data={serviceData.family} />
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-6">
                Why Choose restore.dental as Your Acton Dentist?
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-rabbit">Experienced & Qualified</strong>
                    <p className="text-chocolate-malt">Dr Kartik Patel brings years of experience in all aspects of dentistry</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-rabbit">Convenient Location</strong>
                    <p className="text-chocolate-malt">Located in the heart of Acton, easy access from West London, Ealing, and Hammersmith</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-rabbit">Modern Equipment</strong>
                    <p className="text-chocolate-malt">State-of-the-art technology for accurate diagnosis and comfortable treatment</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-rabbit">Flexible Payment</strong>
                    <p className="text-chocolate-malt">Payment plans available to make dental care affordable</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-burnt-coral/10 via-sky-gray/10 to-[#D4AF37]/10 rounded-2xl p-8 md:p-12 text-center border-2 border-burnt-coral">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-4">
                Ready to Book Your Dental Appointment?
              </h2>
              <p className="text-lg text-chocolate-malt mb-6">
                New patients welcome. Same-day appointments often available.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button size="lg" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild>
                  <a href={whatsappLink}>
                    <WhatsAppIcon className="w-5 h-5 mr-2" />
                    Book on WhatsApp
                  </a>
                </Button>
                <Button size="lg" variant="outline" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" asChild>
                  <a href={phoneLink}>
                    <Phone className="w-5 h-5 mr-2" />
                    Call 07787 814 064
                  </a>
                </Button>
              <EnquireButton slug="general-checkup" about="General Checkup" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" testId="cta-enquire-btn" />
              </div>
            </div>

            {/* Other Services */}
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-rabbit mb-6 text-center">
                Our Specialist Services
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/invisalign-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all">
                  <h3 className="font-bold text-rabbit mb-2">Invisalign</h3>
                  <p className="text-sm text-chocolate-malt">150+ cases completed by Dr Kartik Patel</p>
                </Link>
                <Link to="/composite-bonding-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all">
                  <h3 className="font-bold text-rabbit mb-2">Composite Bonding</h3>
                  <p className="text-sm text-chocolate-malt">Transform your smile in one visit</p>
                </Link>
                <Link to="/cosmetic-dentist-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all">
                  <h3 className="font-bold text-rabbit mb-2">Cosmetic Dentistry</h3>
                  <p className="text-sm text-chocolate-malt">Teeth whitening, veneers & more</p>
                </Link>
                <Link to="/emergency-dentist-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all">
                  <h3 className="font-bold text-rabbit mb-2">Emergency Dentist</h3>
                  <p className="text-sm text-chocolate-malt">Same-day appointments available</p>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      <SiteFooter />
      
      {/* Mini Consult NOW Floating Button */}
      <MiniConsultButton />
    </>
  );
};

export default DentistActon;
